import React from 'react'
import { Button, Icon, Modal, Tag } from 'antd'
import moment from 'moment'
import { SUBSCRIPTION_STATUS_LABELS } from '@constants/subscriptionStatuses'
import { MISSING_VALUE, PRODUCT_COLUMNS, productLines } from './itemRows'

import {
    modalWrap,
    modalTitle,
    titleIcon,
    titleText,
    titleMeta,
    summaryBand,
    summaryItem,
    summaryLabel,
    summaryValue,
    warningBand,
    detailsGrid,
    sourceHeader,
    innerGrid,
    section,
    sectionHeader,
    sectionToggle,
    summaryMore,
    checksCollapsed,
    moreToggle,
    sectionChevron,
    sectionBody,
    rowsGrid,
    productTable,
    productRow,
    productCell,
    cellLabel,
    cellValue,
    nameCell,
    detailRow,
    detailLabel,
    detailValue,
    breakValue,
    invalidValue,
    checksGrid,
    checkItem
} from './style.scss'

interface IProps {
    visible: boolean
    sto: any | null
    onClose: () => void
}

const STO_STATUS_LABELS: { [key: string]: string } = {
    active: 'פעיל',
    inactive: 'לא פעיל'
}

const FREQUENCY_LABELS: { [key: string]: string } = {
    monthly: 'חודשי'
}

type SectionKey = 'product' | 'customer' | 'billing' | 'payment' | 'tranzila' | 'subscription'

interface IState {
    recordKey: string | null
    openKeys: SectionKey[]
    // narrow screens only: the secondary summary items and the status checks behind one disclosure
    moreOpen: boolean
}

const DEFAULT_OPEN: SectionKey[] = ['product']
let modalInstances = 0

class StoDetailsModal extends React.PureComponent<IProps, IState> {
    // the operator's expansion belongs to the open record: it resets when the modal is reopened or another STO is
    // selected, and survives any ordinary re-render of the same one
    static getDerivedStateFromProps(props: IProps, state: IState): Partial<IState> | null {
        const recordKey =
            props.visible && props.sto ? `${String(props.sto.terminal_name)}|${String(props.sto.sto_id)}` : null
        return recordKey === state.recordKey ? null : { recordKey, openKeys: DEFAULT_OPEN, moreOpen: false }
    }

    state: IState = { recordKey: null, openKeys: DEFAULT_OPEN, moreOpen: false }

    idPrefix = `sto-details-${++modalInstances}`

    toggleMore = () => this.setState(prev => ({ moreOpen: !prev.moreOpen }))

    toggleSection = (key: SectionKey) => {
        this.setState(prev => ({
            openKeys:
                prev.openKeys.indexOf(key) === -1 ? prev.openKeys.concat(key) : prev.openKeys.filter(k => k !== key)
        }))
    }

    hasValue = (value: any): boolean => value !== null && value !== undefined && value !== ''

    formatDate = (value: any): string => {
        if (!this.hasValue(value)) return '—'
        const parsed = moment(value)
        if (!parsed.isValid()) return String(value)
        const hasTime = String(value).indexOf(':') !== -1
        return parsed.format(hasTime ? 'DD/MM/YYYY HH:mm:ss' : 'DD/MM/YYYY')
    }

    formatDateOnly = (value: any): string => {
        if (!this.hasValue(value)) return '—'
        const parsed = moment(value)
        return parsed.isValid() ? parsed.format('DD/MM/YYYY') : String(value)
    }

    formatMoney = (value: any): string => {
        if (!this.hasValue(value)) return '—'
        const amount = Number(value)
        return isNaN(amount) ? `${value} ₪` : `${amount.toFixed(2)} ₪`
    }

    formatPhone = (client: any): string => {
        const local = [client.phone_area_code, client.phone_number].filter(Boolean).join('-')
        if (!local) return '—'
        return local
    }

    renderValue = (value: any, ltr: boolean = false): React.ReactNode => {
        if (!this.hasValue(value)) return '—'
        const text = String(value)
        return ltr ? <bdi dir="ltr">{text}</bdi> : text
    }

    renderRow = (label: string, value: any, ltr: boolean = false, allowBreak: boolean = false) => (
        <div className={detailRow}>
            <span className={detailLabel}>{label}</span>
            <span className={`${detailValue} ${allowBreak ? breakValue : ''}`}>
                {React.isValidElement(value) ? value : this.renderValue(value, ltr)}
            </span>
        </div>
    )

    renderCheck = (label: string, hasIssue: boolean) => (
        <div className={checkItem}>
            <span>{label}</span>
            <Tag color={hasIssue ? 'red' : 'green'}>
                <Icon type={hasIssue ? 'exclamation-circle' : 'check-circle'} />
                <span>{hasIssue ? 'דורש בדיקה' : 'תקין'}</span>
            </Tag>
        </div>
    )

    renderSection = (key: SectionKey, icon: string, title: string, content: React.ReactNode) => {
        const open = this.state.openKeys.indexOf(key) !== -1
        const buttonId = `${this.idPrefix}-${key}-toggle`
        const panelId = `${this.idPrefix}-${key}-panel`
        return (
            <section className={section}>
                <h3 className={sectionHeader}>
                    <button
                        type="button"
                        id={buttonId}
                        className={sectionToggle}
                        aria-expanded={open}
                        aria-controls={panelId}
                        onClick={() => this.toggleSection(key)}
                    >
                        <Icon type={icon} aria-hidden="true" />
                        <span>{title}</span>
                        <Icon type={open ? 'up' : 'down'} className={sectionChevron} aria-hidden="true" />
                    </button>
                </h3>
                <div id={panelId} role="region" aria-labelledby={buttonId} hidden={!open} className={sectionBody}>
                    {open ? content : null}
                </div>
            </section>
        )
    }

    renderProductTable = (lines: ReturnType<typeof productLines>) => (
        <table className={productTable} role="table">
            <thead role="rowgroup">
                <tr role="row">
                    {PRODUCT_COLUMNS.map(column => (
                        <th key={column.key} role="columnheader" scope="col">
                            {column.label}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody role="rowgroup">
                {lines.map((line, index) => (
                    <tr key={index} role="row" className={productRow}>
                        {PRODUCT_COLUMNS.map(column => {
                            const value = line[column.key]
                            const missing = value === MISSING_VALUE
                            return (
                                <td
                                    key={column.key}
                                    role="cell"
                                    className={`${productCell} ${column.key === 'name' ? nameCell : ''}`}
                                >
                                    <span className={cellLabel} aria-hidden="true">
                                        {column.label}
                                    </span>
                                    <span className={cellValue}>
                                        {missing ? (
                                            value
                                        ) : (
                                            <bdi dir={column.key === 'name' ? 'auto' : 'ltr'}>{value}</bdi>
                                        )}
                                    </span>
                                </td>
                            )
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    )

    render() {
        const { visible, sto, onClose } = this.props
        const { moreOpen } = this.state
        if (!sto) return null

        const client = sto.client || {}
        const item = sto.item || {}
        const lines = productLines(sto)
        const card = sto.card || {}
        const bank = sto.msv || {}
        const totalSubs = sto.total_subs_count || {}
        const clientName = typeof client.name === 'string' ? client.name.trim() : ''
        const hasResolvedClientUserId =
            sto.client_user_id !== null && sto.client_user_id !== undefined && String(sto.client_user_id).trim() !== ''
        const clientIdentityKnown = sto.client_user_id === null || hasResolvedClientUserId
        const invalidClientName = sto.client_user_id === null
        const clientNameValue = !clientName ? (
            <span className={invalidValue}>חוסר מזהה לקוח</span>
        ) : !invalidClientName ? (
            client.name
        ) : (
            <span className={invalidValue}>
                {clientName} <span>(לא תקין)</span>
            </span>
        )
        const emailValue =
            typeof client.email === 'string' && client.email.trim() ? (
                client.email
            ) : (
                <span className={invalidValue}>חוסר דוא״ל</span>
            )
        const isBank = sto.terminal_name === 'mediwhobk' || Boolean(sto.msv)
        const statusLabel = STO_STATUS_LABELS[sto.sto_status] || sto.sto_status || 'לא ידוע'
        const statusColor = sto.sto_status === 'active' ? 'green' : sto.sto_status === 'inactive' ? 'red' : undefined
        const frequencyLabel = FREQUENCY_LABELS[sto.charge_frequency] || sto.charge_frequency
        const paymentMethod = isBank ? 'הוראת קבע בנקאית' : 'כרטיס אשראי'
        const paymentCount = String(sto.sto_payments_number) === '9999' ? 'ללא הגבלה' : sto.sto_payments_number
        const hasSubscription = this.hasValue(sto.user_sub_id)
        const subscriptionStatus =
            hasSubscription && sto.user_sub_status
                ? SUBSCRIPTION_STATUS_LABELS[sto.user_sub_status] || sto.user_sub_status
                : null
        const hasItemName = typeof item.name === 'string' ? Boolean(item.name.trim()) : this.hasValue(item.name)
        const statusConflict = !hasSubscription || Boolean(sto.exist_status_conflict)
        const missingSku = !hasSubscription || !hasItemName || Boolean(sto.missing_sku_key)
        const priceMismatch = !hasSubscription || Boolean(sto.price_mismatch)
        const skuMismatch = !hasSubscription || Boolean(sto.sku_mismatch)
        const multipleSubscriptions = Boolean(totalSubs.exist_multiple_conflict)
        const hasIssues =
            invalidClientName || statusConflict || missingSku || priceMismatch || skuMismatch || multipleSubscriptions

        return (
            <Modal
                visible={visible}
                onCancel={onClose}
                width={1040}
                style={{ maxWidth: 'calc(100vw - 24px)', top: 24 }}
                wrapClassName={modalWrap}
                bodyStyle={{ padding: 0, maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' }}
                footer={<Button onClick={onClose}>סגור</Button>}
                title={
                    <div className={modalTitle} dir="rtl">
                        <span className={titleIcon}>
                            <Icon type={isBank ? 'bank' : 'credit-card'} />
                        </span>
                        <span className={titleText}>
                            <strong>פרטי הוראת חיוב</strong>
                            <span className={titleMeta}>
                                מזהה <bdi dir="ltr">{sto.sto_id}</bdi>
                            </span>
                        </span>
                    </div>
                }
            >
                <div dir="rtl">
                    <div className={summaryBand}>
                        <div className={summaryItem}>
                            <span className={summaryLabel}>סטטוס</span>
                            <span className={summaryValue}>
                                <Tag color={statusColor}>{statusLabel}</Tag>
                            </span>
                        </div>
                        <div className={summaryItem}>
                            <span className={summaryLabel}>סכום חיוב</span>
                            <span className={summaryValue}>
                                {this.renderValue(this.formatMoney(sto.charge_amount), true)}
                            </span>
                        </div>
                        <div
                            id={`${this.idPrefix}-more`}
                            className={`${summaryMore} ${moreOpen ? '' : checksCollapsed}`}
                        >
                            <div className={summaryItem}>
                                <span className={summaryLabel}>החיוב הבא</span>
                                <span className={summaryValue}>
                                    {this.renderValue(this.formatDateOnly(sto.next_charge_date), true)}
                                </span>
                            </div>
                            <div className={summaryItem}>
                                <span className={summaryLabel}>אמצעי תשלום</span>
                                <span className={summaryValue}>{paymentMethod}</span>
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className={moreToggle}
                        aria-expanded={moreOpen}
                        aria-controls={`${this.idPrefix}-more ${this.idPrefix}-checks`}
                        onClick={this.toggleMore}
                    >
                        <span>פרטים ובדיקות סטטוס</span>
                        <Icon type={moreOpen ? 'up' : 'down'} aria-hidden="true" />
                    </button>

                    <div id={`${this.idPrefix}-checks`} className={`${checksGrid} ${moreOpen ? '' : checksCollapsed}`}>
                        {clientIdentityKnown ? this.renderCheck('שם לקוח תקין', invalidClientName) : null}
                        {this.renderCheck('התאמת סטטוס', statusConflict)}
                        {this.renderCheck('מק״ט קיים', missingSku)}
                        {this.renderCheck('התאמת מחיר', priceMismatch)}
                        {this.renderCheck('התאמת מק״ט', skuMismatch)}
                        {this.renderCheck('מנוי יחיד', multipleSubscriptions)}
                    </div>

                    {hasIssues && (
                        <div className={warningBand}>
                            <Icon type="exclamation-circle" />
                            <span>נמצאו פערים בין נתוני החיוב לנתוני המנוי במערכת</span>
                        </div>
                    )}

                    <div className={detailsGrid}>
                        <div className={sourceHeader}>
                            <Icon type="cloud-server" />
                            <span>נתוני טרנזילה</span>
                        </div>

                        {this.renderSection('product', 'shopping', 'פרטי המוצר', this.renderProductTable(lines))}

                        {this.renderSection(
                            'customer',
                            'user',
                            'פרטי הלקוח',
                            <div className={rowsGrid}>
                                {this.renderRow('שם לקוח', clientNameValue)}
                                {this.renderRow('תעודת זהות', client.id, true)}
                                {this.renderRow('כתובת', client.address)}
                                {this.renderRow('טלפון', this.formatPhone(client), true)}
                                {this.renderRow('דוא״ל', emailValue, true, true)}
                            </div>
                        )}

                        {this.renderSection(
                            'billing',
                            'calendar',
                            'פרטי החיוב',
                            <div className={rowsGrid}>
                                {this.renderRow('סכום', this.formatMoney(sto.charge_amount), true)}
                                {this.renderRow('תדירות', frequencyLabel)}
                                {this.renderRow('תחילת חיובים', this.formatDate(sto.first_charge_date), true)}
                                {this.renderRow('חיוב אחרון', this.formatDate(sto.last_charge_date), true)}
                                {this.renderRow('חיוב הבא', this.formatDateOnly(sto.next_charge_date), true)}
                                {this.renderRow('יום חיוב בחודש', sto.charge_dom, true)}
                                {this.renderRow('מספר חיובים', paymentCount, true)}
                            </div>
                        )}

                        {this.renderSection(
                            'payment',
                            isBank ? 'bank' : 'credit-card',
                            'פרטי אמצעי תשלום',
                            <div className={rowsGrid}>
                                {this.renderRow('מסוף', sto.terminal_name, true)}
                                {this.renderRow('סוג אמצעי', paymentMethod)}
                                {isBank ? (
                                    <>
                                        {this.renderRow('קוד בנק', bank.bank_code, true)}
                                        {this.renderRow('קוד סניף', bank.branch_code, true)}
                                        {this.renderRow('מספר חשבון', bank.account_number, true)}
                                    </>
                                ) : (
                                    <>
                                        {this.renderRow('טוקן', card.token, true, true)}
                                        {this.renderRow(
                                            'תוקף כרטיס',
                                            card.expire_month && card.expire_year
                                                ? `${card.expire_month}/${card.expire_year}`
                                                : null,
                                            true
                                        )}
                                        {this.renderRow('ת״ז בעל הכרטיס', card.card_holder_id, true)}
                                    </>
                                )}
                            </div>
                        )}

                        {this.renderSection(
                            'tranzila',
                            'info-circle',
                            'פרטי הוראת החיוב בטרנזילה',
                            <div className={innerGrid}>
                                <div>
                                    {this.renderRow('מזהה חיוב', sto.sto_id, true)}
                                    {this.renderRow('מזהה עסקה ראשונה', sto.sto_first_transaction_id, true)}
                                </div>
                                <div>{this.renderRow('תאריך יצירה', this.formatDate(sto.create_date), true)}</div>
                                <div>{this.renderRow('נוצר על ידי', sto.created_by_user)}</div>
                            </div>
                        )}

                        <div className={sourceHeader}>
                            <Icon type="database" />
                            <span>נתוני המערכת</span>
                        </div>

                        {this.renderSection(
                            'subscription',
                            'profile',
                            'פרטי המנוי במערכת',
                            <div className={innerGrid}>
                                <div>
                                    {this.renderRow('מזהה מנוי', hasSubscription ? sto.user_sub_id : null, true)}
                                    {this.renderRow(
                                        'מזהה חיוב במערכת',
                                        hasSubscription ? sto.user_sub_sto_id : null,
                                        true
                                    )}
                                </div>
                                <div>
                                    {this.renderRow('סטטוס מנוי', subscriptionStatus)}
                                    {this.renderRow('שם מנוי', hasSubscription ? sto.user_sub_name : null)}
                                </div>
                                <div>
                                    {this.renderRow('מק״ט מנוי', hasSubscription ? sto.user_sub_sku : null, true)}
                                    {this.renderRow(
                                        'מחיר מנוי',
                                        this.formatMoney(hasSubscription ? sto.user_sub_amount : null),
                                        true
                                    )}
                                    {this.renderRow('מנויים תואמים', hasSubscription ? totalSubs.total : null, true)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
        )
    }
}

export default StoDetailsModal
